import { TransactionTypeEnum } from '@aviv_price-pages/brand';
import { colorscales } from '@/components/Map/Colorscales';
/**
 * Get the name for a layer's source
 *
 * @param layerId - ID of the layer
 * @return The layer source name
 * @private
 */
export function getLayerSourceName(layerId) {
    return `${layerId}-source`;
}
/**
 * Remove layers and their source from the map
 *
 * @param layers - Array of the layers' ID to remove
 * @param map
 * @private
 */
export function removeLayers(layers, map) {
    layers.forEach((layerId) => {
        map.removeLayer(layerId);
        map.removeSource(getLayerSourceName(layerId));
    });
}
/**
 *
 * @param colorScaleLevels
 * @param minzoom
 * @param maxzoom
 * @returns
 *
 * @private
 */
export function priceLayerBuilder(colorScaleLevels, minzoom, maxzoom) {
    return function (layerId, layerSource, sourceLayer) {
        return {
            id: layerId,
            type: 'fill',
            paint: {
                'fill-color': {
                    property: 'value',
                    stops: colorScaleLevels.map((color, index) => [index + 1, color]),
                    default: '#bbb',
                },
                'fill-opacity': 0.6,
                'fill-outline-color': '#fff',
            },
            source: layerSource,
            'source-layer': sourceLayer,
            minzoom: minzoom,
            maxzoom: maxzoom,
        };
    };
}
/**
 *
 * @param placeId
 * @returns
 *
 * @private
 */
export function placeOutlineLayerBuilder(placeId) {
    return function (layerId, layerSource, sourceLayer) {
        return {
            id: layerId,
            type: 'line',
            paint: {
                'line-color': '#000',
                'line-width': 3,
            },
            source: layerSource,
            'source-layer': sourceLayer,
            filter: ['==', ['get', 'feature_id'], placeId],
        };
    };
}
/**
 *
 * @returns
 *
 * @private
 * @param minzoom
 * @param maxzoom
 */
export function selectionBackgroundLayerBuilder(minzoom, maxzoom) {
    return function (layerId, layerSource, sourceLayer) {
        return {
            id: layerId,
            type: 'fill',
            paint: {
                'fill-color': '#000',
                'fill-opacity': 0,
            },
            source: layerSource,
            'source-layer': sourceLayer,
            minzoom: minzoom,
            maxzoom: maxzoom,
        };
    };
}
/**
 *
 * @returns
 *
 * @private
 * @param minzoom
 * @param maxzoom
 */
export function selectionOutlineLayerBuilder(minzoom, maxzoom) {
    return function (layerId, layerSource, sourceLayer) {
        return {
            id: layerId,
            type: 'line',
            paint: {
                'line-width': 3,
                'line-color': '#000',
            },
            source: layerSource,
            'source-layer': sourceLayer,
            minzoom: minzoom,
            maxzoom: maxzoom,
        };
    };
}
/**
 *
 * @returns
 *
 * @private
 * @param minzoom
 * @param maxzoom
 */
export function selectionIndicatorLayerBuilder(minzoom, maxzoom) {
    return function (layerId, layerSource, sourceLayer) {
        return {
            id: layerId,
            type: 'line',
            paint: {
                'line-width': 1,
                'line-color': '#FFF',
            },
            source: layerSource,
            'source-layer': sourceLayer,
            minzoom: minzoom,
            maxzoom: maxzoom,
        };
    };
}
/**
 * Common function to add a vector tile layer on the map
 *
 * @param arg
 * @param arg.layerId
 * @param arg.tiles Tiles URL
 * @param arg.sourceLayer Layer id in the tilejson source
 * @param arg.minzoom Min zoom of the layer source
 * @param arg.maxzoom Max zoom of the layer source
 * @param arg.layerStyleBuilder Builder of the layer description
 * @param arg.layersArray Array to store the layer
 * @param arg.map Current map
 * @param arg.beforeLayer Layer id before this layer will must be inserted
 *
 * @private
 */
export function addTileLayer({ layerId, tiles, sourceLayer, minzoom, maxzoom, layerStyleBuilder, layersArray, map, beforeLayer, }) {
    if (map.getLayer(layerId)) {
        return;
    }
    const layerSource = getLayerSourceName(layerId);
    const data = {
        minzoom,
        maxzoom,
        type: 'vector',
        tiles,
    };
    map.addSource(layerSource, data);
    map.addLayer(layerStyleBuilder(layerId, layerSource, sourceLayer), beforeLayer);
    layersArray.push(layerId);
}
/**
 * Add a price layer
 *
 * @param arg
 * @param arg.map Current map
 * @param arg.placeId
 * @param arg.layer TileJSON of the current place type
 * @param arg.layersArray
 * @param arg.beforeLayer
 * @param arg.colorScaleLevels ColorScaleLevels for the layer
 *
 * @private
 */
export function addPriceLayer({ map, placeId, layer: { tiles, name, minzoom, maxzoom }, layersArray, beforeLayer, colorScaleLevels, }) {
    const layerId = `prices-${TransactionTypeEnum.SELL}-${name}-${placeId}`;
    addTileLayer({
        layerId,
        tiles,
        sourceLayer: name,
        minzoom,
        maxzoom,
        layerStyleBuilder: priceLayerBuilder(colorScaleLevels, minzoom, maxzoom),
        layersArray,
        map,
        beforeLayer,
    });
}
/**
 *
 * @param arg
 */
export function addAllPriceLayers({ map, placeId, layerCollection, layersArray, beforeLayer, }) {
    for (const layer of layerCollection) {
        if (!layer.boundaryPlace) {
            break;
        }
        const colorScalePriceRanges = Object.values(layer.boundaryPlace.colorScale);
        const colorScaleLevels = colorscales[colorScalePriceRanges.length].levels;
        addPriceLayer({
            map,
            placeId,
            layer,
            colorScaleLevels,
            layersArray,
            beforeLayer,
        });
    }
}
/**
 * Add the outline layer of the current place
 *
 * @param params
 * @param params.map - Current map instance
 * @param params.placeId - Current place ID
 * @param params.layer - TileJSON describing the outline layer
 * @param params.layersArray - Array to track added layers
 * @param params.beforeLayer - Optional ID of the layer to insert before
 */
export function addPlaceOutlineLayer({ map, placeId, layer: { tiles, name, minzoom, maxzoom, type }, layersArray, beforeLayer, }) {
    addTileLayer({
        layerId: `current-place-outline-${name}-${placeId}`,
        tiles,
        sourceLayer: type,
        minzoom,
        maxzoom,
        layerStyleBuilder: placeOutlineLayerBuilder(placeId),
        map,
        layersArray,
        beforeLayer,
    });
}
export function getColorScaleByLevel(map, layerCollection) {
    let colorScalePriceRanges = [];
    let colorScaleLevels = [];
    const zoom = map.getZoom();
    for (const layer of layerCollection) {
        if (layer.boundaryPlace && zoom >= layer.minzoom && zoom <= layer.maxzoom) {
            colorScalePriceRanges = Object.values(layer.boundaryPlace.colorScale);
            colorScaleLevels = colorscales[colorScalePriceRanges.length].levels;
            break;
        }
    }
    return {
        colorScalePriceRanges,
        colorScaleLevels,
    };
}
